import { getPastThreeMonths } from "@components/Common/Function";
import Linegraph from "@components/HomePage/Linegraph";

/**
 * Helper to check if value is empty/null/undefined
 * Handles primitives, strings, and arrays
 */
const isEmpty = (value) => {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

/**
 * useDashboardStatistics Hook
 * Calculates and formats statistics for dashboard tabs
 * 
 * @param {Object} data - Dashboard data from useHomeData hook
 * @returns {Object} Formatted statistics for each dashboard tab
 */
export const useDashboardStatistics = (data) => {
  const { secondarySales, salesOperations, team, wss } = data;

  // Secondary Sales Statistics
  const secondarySalesStats = [
    {
      name: "Secondary Sales",
      value: !isEmpty(secondarySales.secondarySales)
        ? secondarySales.secondarySales
        : null,
      trendGraph:
        secondarySales.plannedData?.length > 0 && (
          <Linegraph
            plannedData={secondarySales.plannedData}
            labels={getPastThreeMonths()}
          />
        ),
      isLoading: isEmpty(secondarySales.secondarySales),
    },
    {
      name: "Target Achieved",
      value: !isEmpty(secondarySales.targetAcheived)
        ? Math.floor(secondarySales.targetAcheived) + "%"
        : null,
      trendGraph:
        secondarySales.targetPlannedData?.length > 0 && (
          <Linegraph
            plannedData={secondarySales.targetPlannedData}
            labels={getPastThreeMonths()}
          />
        ),
      isLoading: isEmpty(secondarySales.targetAcheived),
    },
  ];

  // Sales Operations Statistics
  const salesOperationsStats = [
    {
      name: "Outlets Billed FTM",
      value: salesOperations.outletsBilledFtmCount || null,
      trendGraph:
        salesOperations.outletsBilledFtmCountTrend?.length > 0 && (
          <Linegraph
            plannedData={salesOperations.outletsBilledFtmCountTrend}
            labels={getPastThreeMonths()}
          />
        ),
      isLoading: isEmpty(salesOperations.outletsBilledFtmCount),
    },
    {
      name: "RSM FTM",
      value: salesOperations.rsmFtmSum || null,
      trendGraph:
        salesOperations.rsmFtmSumTrend?.length > 0 && (
          <Linegraph
            plannedData={salesOperations.rsmFtmSumTrend}
            labels={getPastThreeMonths()}
          />
        ),
      rsi: !isEmpty(salesOperations.rsmFtmRsi)
        ? `RSI: ${salesOperations.rsmFtmRsi}`
        : undefined,
      isLoading: isEmpty(salesOperations.rsmFtmSum),
    },
  ];

  // Team Statistics
  const teamStats = [
    {
      name: "Avg TSI Score",
      value: !isEmpty(team.teamsAvgTsiScore)
        ? Math.floor(team.teamsAvgTsiScore)
        : null,
      isLoading: isEmpty(team.teamsAvgTsiScore),
    },
    {
      name: "Avg TSR Score",
      value: !isEmpty(team.teamsAvgTsrScore)
        ? Math.floor(team.teamsAvgTsrScore)
        : null,
      isLoading: isEmpty(team.teamsAvgTsrScore),
    },
    {
      name: "Qualifying for incentive",
      value: !isEmpty(team.teamIncentive)
        ? team.teamIncentive?.[0]?.total === "0"
          ? "0"
          : `${team.teamIncentive?.[0]?.counts}/${team.teamIncentive?.[0]?.total}`
        : null,
      isLoading: isEmpty(team.teamIncentive),
    },
    {
      name: "Qualifying for SOP",
      value: !isEmpty(team.teamSop)
        ? team.teamSop?.[0]?.total === "0"
          ? "0"
          : `${team.teamSop?.[0]?.counts}/${team.teamSop?.[0]?.total}`
        : null,
      isLoading: isEmpty(team.teamSop),
    },
  ];

  // WSS Statistics
  const wssStats = [
    {
      name: "Auto Value FTD",
      value: wss.wssAutoValueFtdSum || null,
      trendGraph:
        wss.wssAutoValueFtdSumTrend?.length > 0 && (
          <Linegraph
            plannedData={wss.wssAutoValueFtdSumTrend}
            labels={getPastThreeMonths()}
          />
        ),
      isLoading: isEmpty(wss.wssAutoValueFtdSum),
    },
    {
      name: "WSS in Block",
      value: wss.wssWssInBlockCount || null,
      isLoading: isEmpty(wss.wssWssInBlockCount),
    },
    {
      name: "Sync Days Exception",
      value: wss.wssDarpanSyncDayExceptionCount || null,
      isLoading: isEmpty(wss.wssDarpanSyncDayExceptionCount),
    },
  ];

  return {
    secondarySalesStats,
    salesOperationsStats,
    teamStats,
    wssStats,
  };
};
